import React, {useCallback, useEffect, useMemo, useState} from 'react';
import I18n from 'i18n-js';
import {Text, View, useWindowDimensions} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';
import Toast from '../../commons/Toast';
import {
  dismissSideEffects,
  fetchTenantPage,
  saveTenantSetting
} from '../../actions/adminToolsActions';
import {fontFamily} from '../../helpers/fontFamily';
import {
  accentColor,
  breakpointPhone,
  dangerColor,
  textColor,
  textMutedColor
} from '../../style/adminTools';
import SettingsSection from './components/SettingsSection';
import AdminPanelShell from './tenantGrid/AdminPanelShell';
import TenantActionDialog from './tenantGrid/TenantActionDialog';
import TenantHeader from './tenantPage/TenantHeader';
import buildHeaderModel from './tenantPage/headerModel';
import useNotice from './tenantPage/useNotice';
import useBillingConfirm from './tenantPage/useBillingConfirm';
import {
  billingDialogMessage,
  billingNoticeOf,
  billingImpactOf,
  isDoubleCharge
} from './tenantPage/billingImpact';
import useTenantActions, {DIALOGS} from './tenantPage/useTenantActions';
import {
  TenantFailurePanel,
  TenantLoadingState,
  TenantRefreshFailedBanner
} from './tenantPage/TenantPageState';
import SideEffectsNotice from './tenantPage/SideEffectsNotice';
import NoticeLayer from './tenantPage/NoticeLayer';
import SettingsSearchBar, {
  SearchNoMatchesPanel,
  groupCountLabelFor
} from './tenantPage/SettingsSearchBar';
import {computeSearch} from './tenantPage/settingsSearch';
import renderCustomerRow from './tenantPage/customerRows';
import {
  buildCustomerDefinitions,
  buildTeamDefinitions,
  readValues
} from './tenantPage/sectionConfig';

const pickMeta = (groupMeta, field) =>
  Object.keys(groupMeta).reduce((picked, id) => {
    if (groupMeta[id][field]) picked[id] = groupMeta[id][field];
    return picked;
  }, {});

const PHONE_TOAST_BOTTOM = 140;
// The page scrolls the window, not the shell's ScrollView; while that ScrollView is overflow:auto it is the
// sticky scrollport and the search bar never pins (GROOV-5187). overflowX clip keeps the horizontal clipping.
const PHONE_STICKY_SCROLL_STYLE = {overflowX: 'clip', overflowY: 'visible'};

const TenantPage = ({tenantId, tenantName, onOpenMenu, onBackToTenants}) => {
  const dispatch = useDispatch();
  const page = useSelector(state => state.adminTenantPage);
  const {width} = useWindowDimensions();
  const phone = width < breakpointPhone;

  const load = useCallback(() => {
    // eslint-disable-next-line no-restricted-syntax -- the thunk dispatches the failure; the panel and banner render from the store
    dispatch(fetchTenantPage(tenantId)).catch(() => {});
  }, [dispatch, tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const deletedFromShow = Boolean(
    page.show &&
      page.show.tenant &&
      page.show.tenant.basicinfo &&
      page.show.tenant.basicinfo.deleted_at
  );
  // A deleted tenant's /settings.json 404s, so settings never load for it — the page is still
  // "loaded" off /show.json alone (header + Restore action; see headerModel.deleted below).
  const hasData =
    page.tenantId === tenantId &&
    Boolean(page.show) &&
    (deletedFromShow || Boolean(page.settings));
  const failed = page.tenantId === tenantId && page.status === 'failed';

  const isGpadmin = hasData && page.show.tenant.logged_in_user === 'gpadmin';
  const hasShopifyStore =
    hasData && page.show.tenant.basicinfo
      ? page.show.tenant.basicinfo.has_shopify_store === true
      : false;
  const team = useMemo(
    () => buildTeamDefinitions({isGpadmin, hasShopifyStore}),
    // labels are resolved at build time, so a locale change must rebuild them
    [isGpadmin, hasShopifyStore, I18n.locale]
  );
  const customer = useMemo(
    () => buildCustomerDefinitions({settings: hasData ? page.settings : null}),
    [hasData, page.settings, I18n.locale]
  );
  const values = useMemo(
    () => (hasData ? readValues(page.settings, page.show) : {}),
    [hasData, page.settings, page.show]
  );
  const groupDescriptions = useMemo(
    () => pickMeta(team.groupMeta, 'description'),
    [team]
  );
  const groupBadges = useMemo(() => pickMeta(team.groupMeta, 'badge'), [team]);
  const customerDescriptions = useMemo(
    () => pickMeta(customer.groupMeta, 'description'),
    [customer]
  );
  const customerCountLabels = useMemo(
    () => pickMeta(customer.groupMeta, 'countLabel'),
    [customer]
  );
  const [query, setQuery] = useState('');
  const [userOpen, setUserOpen] = useState(() => new Set());
  const [searchOverrides, setSearchOverrides] = useState({});

  const search = useMemo(
    () => computeSearch([...team.definitions, ...customer.definitions], query),
    [team, customer, query]
  );

  const changeQuery = useCallback(next => {
    setQuery(next);
    setSearchOverrides({});
  }, []);
  const clearQuery = useCallback(() => changeQuery(''), [changeQuery]);

  const expandedGroups = useMemo(() => {
    if (!search.active) return userOpen;
    const open = new Set(search.openGroups);
    Object.keys(searchOverrides).forEach(id => {
      if (searchOverrides[id]) open.add(id);
      else open.delete(id);
    });
    return open;
  }, [search, userOpen, searchOverrides]);

  const toggleGroup = useCallback(
    (id, next) => {
      if (search.active) {
        setSearchOverrides(prev => ({...prev, [id]: next}));
        return;
      }
      setUserOpen(prev => {
        const copy = new Set(prev);
        if (next) copy.add(id);
        else copy.delete(id);
        return copy;
      });
    },
    [search.active]
  );

  const searchCountLabels = useMemo(() => {
    const labels = {};
    search.groupCounts.forEach((count, id) => {
      labels[id] = groupCountLabelFor(count);
    });
    return labels;
  }, [search]);

  const displayName =
    tenantName ||
    (hasData &&
      page.show.tenant.basicinfo &&
      page.show.tenant.basicinfo.name) ||
    String(tenantId);

  const {notice, notify, clear: clearNotice} = useNotice();
  const billing = useBillingConfirm();
  const {confirmFirst} = billing;

  const onSave = useCallback(
    async (settingKey, value) => {
      const impact = billingImpactOf(settingKey, value, {
        settings: page.settings,
        show: page.show
      });
      if (impact && !(await confirmFirst(impact, settingKey))) {
        return {ok: false, cancelled: true};
      }
      const result = await dispatch(
        saveTenantSetting(tenantId, settingKey, value)
      );
      if (!result.ok) return result;
      const billingNotice = billingNoticeOf(result, displayName);
      if (billingNotice) {
        notify(billingNotice.message, billingNotice.tone);
      } else {
        notify(
          I18n.t('adminTenantPage.savedFor', {tenant: displayName}),
          'success'
        );
      }
      return result;
    },
    [dispatch, tenantId, displayName, notify, confirmFirst, page]
  );

  const headerModel = useMemo(
    () =>
      buildHeaderModel(
        hasData ? page.settings : null,
        hasData ? page.show : null
      ),
    [hasData, page.settings, page.show]
  );
  const actions = useTenantActions({
    tenantId,
    tenantName: displayName,
    model: headerModel,
    reload: load,
    notify
  });
  const dialogName = headerModel.name || displayName;
  const billingLabel = billing.prompt
    ? (
        [...team.definitions, ...customer.definitions].find(
          definition => definition.key === billing.prompt.settingKey
        ) || {}
      ).label
    : null;
  const dialogConfig = actions.dialog ? DIALOGS[actions.dialog.kind] : null;

  // Every settings write 404s for a deleted tenant, so its settings are never rendered, even
  // when a re-read reports the deletion while earlier settings are still held.
  const hasSettings = hasData && Boolean(page.settings) && !deletedFromShow;

  let body;
  if (hasData) {
    body = (
      <View testID={hasSettings ? 'tenantPageLoaded' : 'tenantPageDeleted'}>
        {failed ? (
          <TenantRefreshFailedBanner onRetry={load} phone={phone} />
        ) : null}
        <TenantHeader
          tenantName={displayName}
          model={headerModel}
          phone={phone}
          testTenantBusy={actions.testBusy}
          onBackToTenants={onBackToTenants}
          onToggleTestTenant={actions.toggleTestTenant}
          onDuplicate={() => actions.openDialog('duplicate')}
          onDelete={() => actions.openDialog('delete')}
          onRestore={() => actions.openDialog('restore')}
        />
        {hasSettings ? (
          <>
            <SettingsSearchBar
              value={query}
              onChange={changeQuery}
              onClear={clearQuery}
              search={search}
              tenantName={displayName}
              phone={phone}
            />
            {search.active && search.totalMatches === 0 ? (
              <SearchNoMatchesPanel
                query={query.trim()}
                onClear={clearQuery}
                phone={phone}
              />
            ) : null}
            <SettingsSection
              band="team"
              testID="teamSettings"
              title={I18n.t('adminTools.teamBandTitle')}
              description={I18n.t('adminTools.teamBandDescription')}
              railLabel={I18n.t('adminTools.teamOnly')}
              chipLabel={I18n.t('adminTools.teamOnly')}
              definitions={team.definitions}
              values={values}
              onSave={onSave}
              groupDescriptions={groupDescriptions}
              groupBadges={groupBadges}
              groupCountLabels={search.active ? searchCountLabels : undefined}
              expandedGroups={expandedGroups}
              onToggleGroup={toggleGroup}
              dimmedGroups={search.dimmedGroups}
              matchKeys={search.rowMatches}
              highlightQuery={search.needle}
              renderCustom={renderCustomerRow}
            />
            <Text
              testID="tenantPageBridge"
              style={{
                fontFamily: fontFamily.font600,
                fontSize: 13,
                color: textMutedColor,
                marginVertical: 12
              }}
            >
              {I18n.t('adminTools.bridgeLine', {tenant: displayName})}
            </Text>
            <SettingsSection
              band="customer"
              testID="customerSettings"
              title={I18n.t('adminTools.customerBandTitle')}
              description={I18n.t('adminTools.customerBandDescription', {
                tenant: displayName
              })}
              railLabel={I18n.t('adminTools.customerSeesThese')}
              chipLabel={I18n.t('adminTools.customerAlsoSees')}
              definitions={customer.definitions}
              values={values}
              onSave={onSave}
              groupDescriptions={customerDescriptions}
              groupCountLabels={
                search.active ? searchCountLabels : customerCountLabels
              }
              expandedGroups={expandedGroups}
              onToggleGroup={toggleGroup}
              dimmedGroups={search.dimmedGroups}
              matchKeys={search.rowMatches}
              highlightQuery={search.needle}
              renderCustom={renderCustomerRow}
            />
          </>
        ) : null}
      </View>
    );
  } else if (failed) {
    body = (
      <TenantFailurePanel
        tenantName={displayName}
        error={page.error}
        onRetry={load}
        onBack={onBackToTenants}
        phone={phone}
      />
    );
  } else {
    body = <TenantLoadingState tenantName={displayName} />;
  }

  const noticeIsError = Boolean(notice && notice.tone === 'error');
  return (
    <>
      <AdminPanelShell
        onOpenMenu={onOpenMenu}
        scrollStyle={phone ? PHONE_STICKY_SCROLL_STYLE : undefined}
      >
        {body}
      </AdminPanelShell>
      {billing.prompt ? (
        <TenantActionDialog
          testID="billingConfirmDialog"
          sheet={phone}
          message={billingDialogMessage(
            billing.prompt.impact,
            displayName,
            billingLabel
          )}
          confirmLabel={I18n.t('adminTenantPage.billingConfirm')}
          danger={isDoubleCharge(billing.prompt.impact)}
          onConfirm={() => billing.answer(true)}
          onCancel={() => billing.answer(false)}
        />
      ) : null}
      {dialogConfig && !billing.prompt ? (
        <TenantActionDialog
          sheet={phone}
          message={dialogConfig.message(dialogName)}
          confirmLabel={dialogConfig.label()}
          danger={dialogConfig.danger}
          withInput={dialogConfig.withInput}
          inputMatches={
            dialogConfig.withInput
              ? dialogConfig.inputMatches(dialogName)
              : undefined
          }
          busy={actions.busy}
          onConfirm={actions.confirmDialog}
          onCancel={actions.closeDialog}
        />
      ) : null}
      <NoticeLayer
        phone={phone}
        notice={
          hasData && page.lastSideEffects ? (
            <SideEffectsNotice
              effects={page.lastSideEffects.effects}
              onDismiss={() => dispatch(dismissSideEffects())}
            />
          ) : null
        }
      >
        <Toast
          key={notice ? notice.id : 'none'}
          visible={Boolean(notice)}
          message={notice ? notice.message : ''}
          persistent
          noModal
          onHide={clearNotice}
          onClose={noticeIsError ? clearNotice : undefined}
          bottomOffset={phone ? PHONE_TOAST_BOTTOM : null}
          textColor={textColor}
          colors={
            noticeIsError
              ? [dangerColor, dangerColor]
              : [accentColor, accentColor]
          }
        />
      </NoticeLayer>
    </>
  );
};

export default TenantPage;
